import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import api, { API } from '../lib/api';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Minus, Plus, Trash2, ShoppingBag, MessageCircle, Sparkles, Star, X } from 'lucide-react';

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total, clear } = useCart();
  const { settings } = useSettings();

  const [step, setStep] = useState('cart'); // cart | form
  const [name, setName] = useState('');
  const [payment, setPayment] = useState('Depósito');
  const [clientType, setClientType] = useState('Nuevo');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const buildWhatsAppUrl = () => {
    const waNumber = (settings.whatsapp_number || '').replace(/\D/g, '');
    const productLines = items.flatMap((i) => [
      `- ${i.name} x${i.qty} — Q${(i.price * i.qty).toFixed(0)}`,
      `  📷 ${API}/products/${i.id}/image`,
    ]);
    const lines = [
      '🛍️ *NUEVO PEDIDO - Gifts GT*',
      '',
      `👤 *Nombre:* ${name.trim()}`,
      `💳 *Tipo de pago:* ${payment}`,
      `🆕 *Cliente:* ${clientType}`,
      '',
      '🛒 *Productos:*',
      ...productLines,
      '',
      `💰 *Total: Q${total.toFixed(0)}*`,
    ];
    if (notes.trim()) {
      lines.push('', `📝 *Notas:* ${notes.trim()}`);
    }
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(lines.join('\n'))}`;
  };

  const handleCheckout = () => {
    // PRIMERO: validar TODO antes de cualquier acción
    if (!name.trim()) { toast.error('Escribe tu nombre'); return; }
    if (!payment) { toast.error('Selecciona tipo de pago'); return; }
    if (items.length === 0) { toast.error('El carrito está vacío'); return; }
    const waNumber = (settings.whatsapp_number || '').replace(/\D/g, '');
    if (!waNumber) { toast.error('Número de WhatsApp no configurado'); return; }
    if (submitting) return;

    // SEGUNDO: abrir WhatsApp INMEDIATAMENTE dentro del gesto del usuario
    // (no usar await antes — los navegadores móviles bloquean popups post-await)
    const url = buildWhatsAppUrl();
    const win = window.open(url, '_blank', 'noopener,noreferrer');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      // Bloqueador de popups → fallback a misma pestaña
      window.location.href = url;
    }

    // TERCERO: descontar stock en segundo plano (después de WhatsApp abierto)
    setSubmitting(true);
    api.post('/orders/confirm', {
      items: items.map((i) => ({ product_id: i.id, qty: i.qty })),
    }).then(() => {
      toast.success('¡Pedido enviado! 💖 Stock actualizado.');
    }).catch((e) => {
      // El mensaje ya se envió. Solo aviso si stock no se pudo descontar.
      const msg = e?.response?.data?.detail || 'No se pudo actualizar el stock';
      toast.warning(`WhatsApp abierto — ${msg}`, { duration: 8000 });
    }).finally(() => {
      clear();
      setOpen(false);
      setStep('cart');
      setName('');
      setPayment('Depósito');
      setClientType('Nuevo');
      setNotes('');
      setSubmitting(false);
    });
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col cart-pastel-bg" data-testid="cart-drawer">
        {/* Sparkles decorativas en esquinas — pointer-events-none */}
        <Star className="absolute top-4 right-16 h-4 w-4 text-amber-300 animate-twinkle pointer-events-none z-10" fill="currentColor" strokeWidth={0} />
        <Sparkles className="absolute top-20 left-4 h-4 w-4 text-pinky-400 animate-twinkle pointer-events-none z-10" style={{ animationDelay: '1s' }} strokeWidth={2} />
        <Star className="absolute bottom-32 right-6 h-3 w-3 text-lilac-400 animate-twinkle pointer-events-none z-10" fill="currentColor" strokeWidth={0} style={{ animationDelay: '0.5s' }} />
        <SheetHeader className="px-6 py-5 border-b border-pinky-100 bg-pinky-50/60 backdrop-blur-sm relative">
          <SheetTitle className="font-display text-2xl flex items-center gap-2 text-pinky-700 pr-12">
            <ShoppingBag className="h-6 w-6" /> Tu carrito mágico
          </SheetTitle>
          <SheetClose
            data-testid="cart-close-btn"
            aria-label="Cerrar carrito"
            className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-md border border-pinky-200 text-pinky-600 hover:bg-pinky-100 hover:text-pinky-700 transition-all flex items-center justify-center z-20 focus:outline-none focus:ring-2 focus:ring-pinky-400"
          >
            <X className="h-5 w-5" strokeWidth={2.5} />
          </SheetClose>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <Sparkles className="h-16 w-16 text-pinky-300 mb-4" strokeWidth={1.2} />
            <h3 className="font-display text-xl text-slate-800">Tu carrito está vacío</h3>
            <p className="text-sm text-slate-500 mt-2">Explora el catálogo y agrega tus favoritos.</p>
            <Button onClick={() => setOpen(false)} className="mt-6 rounded-full btn-pink">Seguir comprando</Button>
          </div>
        ) : step === 'cart' ? (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((i) => (
                <div key={i.id} data-testid={`cart-item-${i.id}`} className="flex gap-3 p-3 rounded-2xl border border-pinky-100 bg-white">
                  <img src={i.image} alt={i.name} className="h-20 w-20 rounded-xl object-cover bg-pinky-50" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 text-sm line-clamp-2">{i.name}</p>
                    <p className="text-pinky-600 font-bold mt-1">Q{(i.price * i.qty).toFixed(0)}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <button onClick={() => updateQty(i.id, i.qty - 1)} className="h-7 w-7 rounded-full bg-pinky-50 text-pinky-600 flex items-center justify-center" data-testid={`cart-dec-${i.id}`}>
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center">{i.qty}</span>
                      <button onClick={() => updateQty(i.id, i.qty + 1)} className="h-7 w-7 rounded-full bg-pinky-50 text-pinky-600 flex items-center justify-center" data-testid={`cart-inc-${i.id}`}>
                        <Plus className="h-3 w-3" />
                      </button>
                      <button onClick={() => removeItem(i.id)} className="ml-auto text-rose-500 hover:text-rose-600" data-testid={`cart-remove-${i.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-pinky-100 px-6 py-5 space-y-3 bg-pinky-50/60">
              <div className="flex justify-between items-center">
                <span className="text-slate-600 font-semibold">Total</span>
                <span data-testid="cart-total" className="font-display text-3xl text-pinky-600 font-bold">Q{total.toFixed(0)}</span>
              </div>
              <Button data-testid="cart-next-btn" onClick={() => setStep('form')} className="btn-pink w-full rounded-full h-12 font-bold">
                Continuar al pedido
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              <div>
                <Label htmlFor="cart-name">Nombre del cliente *</Label>
                <Input id="cart-name" data-testid="cart-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre completo" className="mt-1 rounded-xl" />
              </div>

              <div>
                <Label>Tipo de pago *</Label>
                <RadioGroup value={payment} onValueChange={setPayment} className="mt-2 space-y-2" data-testid="cart-payment-group">
                  {['Depósito', 'Pago contra entrega', 'Visa Link'].map((p) => (
                    <div key={p} className="flex items-center gap-3 p-3 rounded-xl border border-pinky-100 hover:bg-pinky-50 transition-colors">
                      <RadioGroupItem value={p} id={`pay-${p}`} />
                      <Label htmlFor={`pay-${p}`} className="flex-1 cursor-pointer font-medium text-slate-700">{p}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label>¿Cliente nuevo o recurrente? *</Label>
                <RadioGroup value={clientType} onValueChange={setClientType} className="mt-2 grid grid-cols-2 gap-2" data-testid="cart-client-type">
                  {['Nuevo', 'Recurrente'].map((c) => (
                    <div key={c} className="flex items-center gap-2 p-3 rounded-xl border border-pinky-100 hover:bg-pinky-50 transition-colors">
                      <RadioGroupItem value={c} id={`ct-${c}`} />
                      <Label htmlFor={`ct-${c}`} className="cursor-pointer font-medium text-slate-700">{c}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div>
                <Label htmlFor="cart-notes">Notas (opcional)</Label>
                <Textarea
                  id="cart-notes"
                  data-testid="cart-notes-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Dirección, comentarios, regalo para..."
                  rows={2}
                  className="mt-1 rounded-xl"
                />
              </div>

              <div className="p-4 rounded-2xl bg-pinky-50 border border-pinky-100">
                <p className="text-sm text-slate-600">Resumen</p>
                <ul className="mt-2 text-sm space-y-1">
                  {items.map((i) => (
                    <li key={i.id} className="flex justify-between text-slate-700">
                      <span className="truncate pr-2">{i.name} x{i.qty}</span>
                      <span className="font-semibold">Q{(i.price * i.qty).toFixed(0)}</span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-pinky-200 mt-3 pt-3 flex justify-between font-bold text-slate-800">
                  <span>Total</span>
                  <span className="text-pinky-600 text-lg">Q{total.toFixed(0)}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-pinky-100 px-6 py-5 flex gap-2">
              <Button variant="outline" onClick={() => setStep('cart')} className="rounded-full flex-1" data-testid="cart-back-btn">Atrás</Button>
              <Button
                data-testid="cart-send-whatsapp-btn"
                onClick={handleCheckout}
                disabled={!name.trim() || submitting}
                className="btn-emerald rounded-full flex-[2] font-bold h-12 gap-2"
              >
                <MessageCircle className="h-5 w-5" /> Enviar por WhatsApp
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
